import React, { FunctionComponent, useRef } from 'react';
import { Object3D, Raycaster, Intersection, Group, Object3DEventMap } from 'three';
import { hasEntityTapAction, transitionAdjustedRenderOrder } from '../../utils';
import { useDataStore, useUserStore } from '../../zustand';
import EntityLevelFadeGroup from '../fadeGroups/EntityLevelFadeGroup';
import { IText3d, IText3dReactProps } from '../r3f-components/component-data-structure';
import { EntityErrorBoundary } from '../r3f-components/components/boundaries';
import { useGetAdjustedMemoSpatialProps, useGetSubscribedSceneTransitionCategoryRef, useGetSubscribedSceneTransitionPhaseRef, useHocPointerDown, useOnEntityTestClick, useResetEntityTransformsOnPlacement } from './hocCustomHooks';
import { useInteraction } from '../TrackerGroups/WorldXRTracker/webxr-hooks';
import { useEnableObjectLayer, useGetSceneIndexBySceneId } from './hocCustomHooks';

interface IText3dWrapped {
	id: string;
	sceneId: string;
	screenRelative?: boolean;
	raycast?: (_: Raycaster, intersects: Intersection<Object3D<Object3DEventMap>>[]) => void;
}

const withText3dBehaviour = (WrappedText3d: FunctionComponent<IText3dReactProps>): FunctionComponent<IText3dWrapped> => {
	const FuncComp: FunctionComponent<IText3dWrapped> = ({ id, sceneId, screenRelative = false, raycast }) => {
		const pointerIsDownRef = useRef(false);
		const pointerGroupRef = useRef<Group>(null)
		const text3d = useDataStore(({ componentsById }) => componentsById?.[id] as IText3d) || {};
		const shadowsEnabled = useDataStore(({ shadowsEnabled }) => shadowsEnabled ?? true);
		const sceneTransCatRef = useGetSubscribedSceneTransitionCategoryRef(sceneId);
		const sceneTransPhaseRef = useGetSubscribedSceneTransitionPhaseRef(sceneId);
		const sceneIndex = useGetSceneIndexBySceneId(sceneId);
		const isGrabAndGoActive = useUserStore(({ isGrabNGoActive }) => isGrabNGoActive);

		useOnEntityTestClick({ sceneId, entity: text3d, sceneTransCatRef, sceneTransPhaseRef });

		useEnableObjectLayer({ object3d: pointerGroupRef, enabled: hasEntityTapAction(text3d), layer: typeof sceneIndex === 'undefined' ? undefined : sceneIndex + 1 });

		// Ensures that any transforms due to previous animation presets are cleared out
		useResetEntityTransformsOnPlacement(id);

		// Get entity values
		const renderOrder = transitionAdjustedRenderOrder(sceneTransCatRef, (text3d?.renderOrder ?? 0) * 20);
		const { position, scale, rotation, zeroTuple3 } = useGetAdjustedMemoSpatialProps(text3d, screenRelative);
		const { material, opacity, origScale, textAlignment, fontFamily,text, fontSize, fontRgba, depth, curveSegments, bevelEnabled, bevelOffset, bevelSegments, bevelSize, bevelThickness, castShadow = false, receiveShadow = false, billboard } = text3d;

		const onPointerDown = useHocPointerDown({ pointerIsDownRef, sceneTransCatRef, sceneTransPhaseRef, sceneId, entity: text3d, screenRelative });

		useInteraction(pointerGroupRef, 'onSelect', e => {
			onPointerDown(e.intersections)
			pointerIsDownRef.current = false;
		})

        return (
			<EntityLevelFadeGroup name={`fadeGroup-${id}`} sceneId={sceneId} position={zeroTuple3} entityId={id} screenRelative={screenRelative || false}>
				<EntityErrorBoundary renderOrder={renderOrder} scale={[1, 1, 1]} rotation={rotation} position={position}>
					<group name={`toggleVisibilityGroup-${id}`}>
						<group ref={pointerGroupRef} name={`sceneId_${sceneId}`} onPointerUp={() => (pointerIsDownRef.current = false)}>
							<WrappedText3d
								id={id}
								scale={scale}
								rotation={rotation}
								position={position}
								renderOrder={renderOrder}
								origScale={origScale}
								textAlignment={textAlignment}
								fontFamily={fontFamily}	
								fontSize={fontSize}
								text={text}
								fontRgba={fontRgba}
								depth={depth}
								curveSegments={curveSegments}
								bevelEnabled={bevelEnabled}
								material={material}
								bevelThickness={bevelThickness}
								bevelSize={bevelSize}
								bevelOffset={bevelOffset}
								bevelSegments={bevelSegments}
								castShadow={shadowsEnabled && castShadow}
								receiveShadow={shadowsEnabled && receiveShadow}
								opacity={opacity}
								raycast={raycast}
								onPointerDown={(e) => onPointerDown(e.intersections)}
								animationTransformGroupPrefix='transformGroup-'
								billboard={billboard}
								isCameraOrbiting={isGrabAndGoActive}
								rotateNoBillboardOuterGroup={false}
							/>
						</group>
					</group>
				</EntityErrorBoundary>
			</EntityLevelFadeGroup>

		);
	};
	return FuncComp;
};

export default withText3dBehaviour;
